import { makeAutoObservable, runInAction } from 'mobx';
import { EmfitAPI } from '../services/BaseAPI';
import { STATUS_API, Locales } from 'src/config';
import { SessionStore } from './SessionStore';
import { getStorageKey } from 'src/storage';
import { LocalizationService } from '../services/LocalizationService';

export interface Timezone {
  zone_id: number;
  zone_name: string;
}

export interface UserSettings {
  locale?: string;
  timezone?: string;
  time_format?: string;
  date_format?: string;
  timezone_id?: number;
  masterUser?: boolean;
}

class UserStoreClass {
  userSettings: UserSettings | null = null;
  timezones: Timezone[] = [];
  loading = false;
  error: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  async fetchUserSettings(): Promise<void> {
    // For group users, locale comes from local storage, but timezone is
    // inherited from the facility owner's account (matches qs-status's
    // Angular reference: UserService.getUserTimezone() calls
    // GET /v1/user/get-owner for non-master users).
    if (!SessionStore.isMasterUser) {
      let timezone: string | undefined;
      try {
        const owner = await EmfitAPI.get<UserSettings>(
          `${STATUS_API}/v1/user/get-owner`
        );
        timezone = owner?.timezone;
      } catch (error) {
        console.error('Failed to fetch owner timezone:', error);
      }

      runInAction(() => {
        this.userSettings = {
          locale: localStorage.getItem(getStorageKey('lang')) || 'en-US',
          masterUser: false,
          timezone,
        };
        this.loading = false;
      });
      return;
    }

    // For master users, fetch from the API
    this.loading = true;
    this.error = null;

    try {
      const user = await EmfitAPI.get<UserSettings>(`${STATUS_API}/v1/user`);
      runInAction(() => {
        this.userSettings = { ...user, masterUser: true };
        this.loading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error
            ? error.message
            : 'Failed to fetch user settings';
        this.loading = false;
      });
      console.error('Failed to fetch user settings:', error);
    }
  }

  async fetchTimezones(): Promise<void> {
    try {
      const zones = await EmfitAPI.get<Timezone[]>(`${STATUS_API}/v1/zones`);
      runInAction(() => {
        this.timezones = zones || [];
      });
    } catch (error) {
      console.error('Failed to fetch timezones:', error);
    }
  }

  async updateSettings(settings: UserSettings): Promise<boolean> {
    // For group users, only update locale locally
    if (!SessionStore.isMasterUser) {
      if (settings.locale) {
        localStorage.setItem(getStorageKey('lang'), settings.locale);
        LocalizationService.locale = settings.locale as Locales;
      }
      runInAction(() => {
        if (this.userSettings) {
          this.userSettings.locale = settings.locale;
        }
      });
      return true;
    }

    // For master users, save to API
    this.loading = true;
    this.error = null;

    try {
      const result = await EmfitAPI.post<{ success: boolean }>(
        `${STATUS_API}/v1/user/update-information`,
        {
          body: JSON.stringify({
            locale: settings.locale,
            timezone: settings.timezone,
            time_format: settings.time_format,
            date_format: settings.date_format,
            timezone_id: settings.timezone_id,
          }),
        }
      );

      runInAction(() => {
        this.loading = false;
      });

      if (result?.success) {
        // Update locale in localStorage and LocalizationService if changed
        if (settings.locale) {
          localStorage.setItem(getStorageKey('lang'), settings.locale);
          LocalizationService.locale = settings.locale as Locales;
        }
        await this.fetchUserSettings();
        return true;
      }
      return false;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error ? error.message : 'Failed to update settings';
        this.loading = false;
      });
      console.error('Failed to update settings:', error);
      return false;
    }
  }

  async updatePassword(
    currentPassword: string,
    newPassword: string
  ): Promise<{
    success: boolean;
    error?: string;
  }> {
    this.loading = true;
    this.error = null;

    // Choose the correct endpoint based on user type
    const endpoint = SessionStore.isMasterUser
      ? `${STATUS_API}/v1/user/update-password`
      : `${STATUS_API}/v1/group-user/update-password`;

    try {
      const result = await EmfitAPI.post<{ success: boolean }>(endpoint, {
        body: JSON.stringify({
          password: currentPassword,
          newpassword: newPassword,
        }),
      });

      runInAction(() => {
        this.loading = false;
      });

      if (result?.success) {
        return { success: true };
      }
      return { success: false, error: 'Wrong password' };
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error ? error.message : 'Failed to update password';
        this.loading = false;
      });
      console.error('Failed to update password:', error);
      return { success: false, error: 'Something went wrong' };
    }
  }

  getTimezoneNameById(id: number): string | undefined {
    const zone = this.timezones.find((tz) => tz.zone_id === id);
    return zone?.zone_name;
  }

  /** IANA timezone name for the account, e.g. "Europe/Helsinki". Falls back to UTC. */
  get timezoneName(): string {
    return this.userSettings?.timezone || 'UTC';
  }

  /**
   * Whether we actually know the account's timezone.
   *
   * Device schedule times are stored as UTC HH:MM and must be converted for
   * display and back on save. Getting that wrong shifts a facility's night
   * window by its whole UTC offset, so callers must not edit schedule times
   * on the `timezoneName` UTC fallback - check this first.
   *
   * It is false for non-admin group users: GET /v1/user/get-owner returns an
   * empty array unless the caller is an admin (qs-status
   * users/user.controller.ts getOwner), so a caregiver never receives the
   * facility owner's timezone.
   */
  get hasKnownTimezone(): boolean {
    return !!this.userSettings?.timezone;
  }

  get isMasterUser(): boolean {
    return SessionStore.isMasterUser;
  }

  /**
   * Drops everything belonging to the current login. Called from
   * resetStores() on logout, so the next login starts empty.
   */
  reset(): void {
    this.userSettings = null;
    this.timezones = [];
    this.loading = false;
    this.error = null;
  }
}

export const UserStore = new UserStoreClass();
